import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { ClipboardList, User, Building2, Calendar, Package, RefreshCw, Eye, Bluetooth } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { useToast } from '@/components/ui/use-toast';
import { getRecolecciones } from '../services/deposito.services';
import ModalDetalleRecoleccion from './ModalDetalleRecoleccion';
import Pagination from '@/components/Pagination';

const ITEMS_PER_PAGE = 15;

const PendientesEntrega = ({ idPlanta, plantaNombre }) => {
  const { toast } = useToast();
  const [recolecciones, setRecolecciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [busqueda, setBusqueda] = useState('');

  const [detalleOpen, setDetalleOpen] = useState(false);
  const [itemDetalle, setItemDetalle] = useState(null);

  const cargar = useCallback(async (page = 1, filtro = busqueda) => {
    if (!idPlanta) return;
    setLoading(true);
    try {
      const response = await getRecolecciones({
        idPlanta,
        estado: 'verificado',
        limit: ITEMS_PER_PAGE,
        offset: page,
        ...(filtro.trim() && {
          'empleado.nombre': filtro.trim(),
        }),
      });

      const data = response?.data || [];
      // Ordenar de más antigua a más reciente
      data.sort((a, b) => new Date(a.createdAt) - new Date(b.createdAt));

      setRecolecciones(data);
      setTotalItems(response?.totalRecords ?? data.length);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error cargando pendientes:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron cargar las recolecciones pendientes',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [idPlanta, busqueda, toast]);

  useEffect(() => {
    cargar(1);
  }, [idPlanta]);

  const handleBuscar = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    cargar(1, busqueda);
  };

  const handleVerDetalle = (rec) => {
    setItemDetalle(rec);
    setDetalleOpen(true);
  };

  const diasDesde = (fecha) => {
    if (!fecha) return null;
    const diff = Math.floor((Date.now() - new Date(fecha)) / 86_400_000);
    return diff;
  };

  const urgencyClass = (dias) => {
    if (dias === null) return '';
    if (dias >= 7) return 'text-red-600 dark:text-red-400';
    if (dias >= 3) return 'text-orange-500 dark:text-orange-400';
    return 'text-muted-foreground';
  };

  return (
    <div className="space-y-4">
      {/* Header + buscador */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2 text-xl">
                <ClipboardList className="h-5 w-5 text-orange-500" />
                Entregas Pendientes
                {!loading && (
                  <Badge variant="secondary" className="ml-1 text-sm">
                    {totalItems}
                  </Badge>
                )}
              </CardTitle>
              <p className="text-sm text-muted-foreground mt-1">
                Recolecciones verificadas y listas para entregar — de más antigua a más reciente
              </p>
            </div>
            <div className="flex gap-2">
              <form onSubmit={handleBuscar} className="flex gap-2">
                <Input
                  placeholder="Buscar por empleado..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                  className="w-52"
                />
                <Button type="submit" variant="outline" size="icon">
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </form>
              <Button
                variant="outline"
                size="icon"
                onClick={() => cargar(currentPage)}
                disabled={loading}
                title="Actualizar"
              >
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Listado */}
      {loading && recolecciones.length === 0 ? (
        <Card>
          <CardContent className="py-16 flex items-center justify-center text-muted-foreground gap-2">
            <RefreshCw className="h-5 w-5 animate-spin" />
            Cargando pendientes...
          </CardContent>
        </Card>
      ) : recolecciones.length === 0 ? (
        <Card>
          <CardContent className="py-16 text-center text-muted-foreground">
            <ClipboardList className="h-12 w-12 mx-auto mb-3 opacity-25" />
            <p className="font-medium">No hay entregas pendientes</p>
            <p className="text-sm mt-1">Todas las recolecciones verificadas han sido entregadas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {recolecciones.map((rec, idx) => {
            const dias = diasDesde(rec.createdAt);

            return (
              <motion.div
                key={rec._id || rec.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.03 }}
              >
                <Card className="hover:shadow-md transition-shadow">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      {/* Número de orden visual */}
                      <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center text-xs font-bold text-orange-600 dark:text-orange-400">
                        {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                      </div>

                      {/* Consecutivo + estado */}
                      <div className="w-36 shrink-0">
                        <p className="font-semibold text-sm font-mono">{rec.consecutivo || 'N/A'}</p>
                        <Badge variant="outline" className="text-orange-600 border-orange-400 text-xs mt-0.5">
                          Pendiente entrega
                        </Badge>
                      </div>

                      {/* Empleado */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-1.5 text-sm font-medium truncate">
                          <User className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                          {rec.empleado?.nombre} {rec.empleado?.apellido}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-muted-foreground mt-0.5">
                          <span>CC {rec.empleado?.cedula}</span>
                          {rec.empleado?.area?.nombre && (
                            <>
                              <span>·</span>
                              <span className="flex items-center gap-1">
                                <Building2 className="h-3 w-3" />
                                {rec.empleado.area.nombre}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Prendas */}
                      <div className="text-center shrink-0 hidden sm:block">
                        <div className="flex items-center gap-1 text-sm font-semibold">
                          <Package className="h-3.5 w-3.5 text-muted-foreground" />
                          {rec.cantidad_prendas ?? 0}
                        </div>
                        <p className="text-xs text-muted-foreground">prendas</p>
                      </div>

                      {/* Fecha + días */}
                      <div className="text-right shrink-0 hidden md:block">
                        <div className="flex items-center gap-1 text-xs text-muted-foreground justify-end">
                          <Calendar className="h-3 w-3" />
                          {rec.createdAt
                            ? new Date(rec.createdAt).toLocaleDateString('es-CO', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                              })
                            : 'N/A'}
                        </div>
                        {dias !== null && (
                          <p className={`text-xs font-medium mt-0.5 ${urgencyClass(dias)}`}>
                            {dias === 0 ? 'Hoy' : `Hace ${dias} día${dias !== 1 ? 's' : ''}`}
                          </p>
                        )}
                      </div>

                      {/* Acciones */}
                      <div className="shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleVerDetalle(rec)}
                          className="flex items-center gap-1.5"
                        >
                          <Eye className="h-3.5 w-3.5" />
                          Ver detalle
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}

          <Pagination
            currentPage={currentPage}
            totalItems={totalItems}
            itemsPerPage={ITEMS_PER_PAGE}
            onPageChange={(page) => cargar(page)}
          />
        </div>
      )}

      <ModalDetalleRecoleccion
        isOpen={detalleOpen}
        onClose={() => {
          setDetalleOpen(false);
          setItemDetalle(null);
        }}
        recoleccion={itemDetalle}
      />
    </div>
  );
};

export default PendientesEntrega;
